// RITA-1543 — Welche Angaben braucht eine Vorlage, und welche fehlen beim Berater?
//
// Fehlt ein Wert, bleibt die Stelle in der Vorschau leer und das Fenster nennt die Angabe mit dem Ort,
// an dem man sie einträgt (Julia 19.10.2026, „1 ja“). Nicht gemeldet werden die „abweichenden“ Angaben –
// Abweichender Vertragsinhaber und abweichende Rechnungsadresse: Leer heißt dort „keine Abweichung“, nicht
// „fehlt“ (RITA-1514: leer bleibt leer; Entscheidung Julia 19.10.2026). Das heutige Datum fehlt nie.
//
// RITA-1552 (Skizze Rücklauf, Bild F b; F9): Auf der Bearbeiten-Seite sind die Berater-Daten schon Text. Die Rückfrage
// „Beim Anlegen fehlten Angaben“ kennt deshalb nur, was beim Anlegen des Entwurfs leer war (`advisor_values`), mit
// denselben Ausnahmen; von Hand gefüllte Lücken erkennt sie nicht (Grenze, im Wortlaut ehrlich benannt).
import type { Block, Inlineknoten, Vertragsdokument } from '../../templates/contractEditor/model/types';
import { Bearbeitername } from './vertragPlatzhalterwerte';

interface Angabe {
  bezeichnung: string;
  /** Wo man die Angabe einträgt. */
  ort: string;
}

const beimBerater = (bereich: string) => `beim Berater unter „${bereich}“ eintragen`;
const PERSOENLICHES = beimBerater('Persönliches');
const VERTRAG = beimBerater('Vertrag');
const FINANZEN = beimBerater('Finanzen');
const SYSTEME = beimBerater('Systeme');
const BENUTZER = 'unter Einstellungen › Benutzer eintragen';

/** Nie als fehlend melden (siehe Kopf). */
const NIE_FEHLEND = new Set(['VERTRAGSINHABER', 'RA_STRASSE', 'RA_PLZ', 'RA_ORT', 'RA_LAND', 'HEUTIGES_DATUM']);

const ANGABEN: Readonly<Record<string, Angabe>> = {
  ANREDE: { bezeichnung: 'Anrede (Geschlecht)', ort: PERSOENLICHES },
  PERSOENLICHE_ANREDE: { bezeichnung: 'Persönliche Anrede (Vorname und Geschlecht)', ort: PERSOENLICHES },
  VORNAME: { bezeichnung: 'Vorname', ort: PERSOENLICHES },
  NACHNAME: { bezeichnung: 'Nachname', ort: PERSOENLICHES },
  GEBURTSTAG: { bezeichnung: 'Geburtstag', ort: PERSOENLICHES },
  GESCHLECHT: { bezeichnung: 'Geschlecht', ort: PERSOENLICHES },
  TELEFON: { bezeichnung: 'Telefon', ort: PERSOENLICHES },
  MOBILTELEFON: { bezeichnung: 'Mobiltelefon', ort: PERSOENLICHES },
  FAX: { bezeichnung: 'Fax', ort: PERSOENLICHES },
  GESCHAEFTSEMAIL: { bezeichnung: 'Geschäftliche E-Mail', ort: PERSOENLICHES },
  PRIVATEEMAIL: { bezeichnung: 'Private E-Mail', ort: PERSOENLICHES },
  STRASSE: { bezeichnung: 'Straße', ort: PERSOENLICHES },
  PLZ: { bezeichnung: 'PLZ', ort: PERSOENLICHES },
  ORT: { bezeichnung: 'Ort', ort: PERSOENLICHES },
  LAND: { bezeichnung: 'Land', ort: PERSOENLICHES },
  STATUS: { bezeichnung: 'Status', ort: VERTRAG },
  RANG: { bezeichnung: 'Rang', ort: VERTRAG },
  LEVEL: { bezeichnung: 'Level', ort: VERTRAG },
  FIRMA: { bezeichnung: 'Firma', ort: VERTRAG },
  VERTRAGSART: { bezeichnung: 'Vertragsart', ort: VERTRAG },
  VERTRAGSPARTNER: {
    bezeichnung: 'Vertragspartner (Anrede und Name oder abweichender Vertragsinhaber)',
    ort: 'beim Berater unter „Persönliches“ bzw. „Vertrag“ eintragen',
  },
  BERATERNUMMER: { bezeichnung: 'Beraternummer', ort: VERTRAG },
  MANDATSREFERENZ: { bezeichnung: 'Mandatsreferenz', ort: VERTRAG },
  STARTDATUM: { bezeichnung: 'Startdatum', ort: VERTRAG },
  TEAMLEITER: { bezeichnung: 'Teamleiter', ort: VERTRAG },
  MYJACK_UNIT: { bezeichnung: 'myJACK Unit', ort: SYSTEME },
  MYJACK_LOGIN: { bezeichnung: 'myJACK Login', ort: SYSTEME },
  KREDITORENNUMMER: { bezeichnung: 'Kreditorennummer', ort: FINANZEN },
  STEUERNUMMER: { bezeichnung: 'Steuernummer', ort: FINANZEN },
  USTID: { bezeichnung: 'Umsatzsteuer ID', ort: FINANZEN },
  IBAN: { bezeichnung: 'IBAN', ort: FINANZEN },
  BANKNAME: { bezeichnung: 'Bankname', ort: FINANZEN },
  BIC: { bezeichnung: 'BIC', ort: FINANZEN },
};

/** Bezeichnung einer Angabe für Hinweise, z. B. „Straße“ (RITA-1547: geänderte Berater-Daten). */
export function angabeBezeichnung(schluessel: string): string {
  return ANGABEN[schluessel]?.bezeichnung ?? schluessel;
}

/** Auch ein teilweise gefüllter Beratername darf fehlende Anrede oder Namensbestandteile nicht verdecken. */
export function fehlendeVertragspartnerAngaben(werte: Readonly<Partial<Record<string, string>>>): string[] {
  if ((werte.VERTRAGSINHABER ?? '').trim()) return [];
  return ['ANREDE', 'VORNAME', 'NACHNAME'].filter((schluessel) => !(werte[schluessel] ?? '').trim());
}

function inlinePlatzhalter(inhalt: Inlineknoten[], ziel: string[]): void {
  inhalt.forEach((knoten) => {
    if (knoten.typ === 'platzhalter') ziel.push(knoten.schluessel);
  });
}

function blockPlatzhalter(bloecke: Block[], ziel: string[]): void {
  bloecke.forEach((block) => {
    if (block.typ === 'absatz') inlinePlatzhalter(block.inhalt, ziel);
    if (block.typ === 'liste') block.eintraege.forEach((eintrag) => inlinePlatzhalter(eintrag, ziel));
  });
}

/** Alle Platzhalter eines Dokuments (Kopfzeile, Text, Fußzeile), jeder einmal, in Reihenfolge des Auftretens. */
export function verwendetePlatzhalter(dokument: Vertragsdokument): string[] {
  const alle: string[] = [];
  blockPlatzhalter(dokument.kopf.inhalt, alle);
  blockPlatzhalter(dokument.text, alle);
  blockPlatzhalter(dokument.fuss.inhalt, alle);
  return Array.from(new Set(alle));
}

export interface Dokumentangaben {
  /** z. B. „im Anschreiben“, „im Vertrag“ */
  stelle: string;
  dokument: Vertragsdokument;
  /** Platzhalter, die nicht mehr im Katalog stehen; das Lesen macht sie zu Text „{{SCHLUESSEL}}“. */
  unbekanntePlatzhalter?: string[];
}

export interface FehlendeAngabe {
  schluessel: string;
  bezeichnung: string;
  ort: string;
  /** Wo die Angabe gebraucht wird, z. B. ['im Anschreiben', 'im Vertrag']. */
  stellen: string[];
}

function bearbeiterAngabe(bearbeiter: Bearbeitername): Angabe | null {
  const vorname = !!(bearbeiter.vorname ?? '').trim();
  const nachname = !!(bearbeiter.nachname ?? '').trim();
  if (vorname && nachname) return null;
  if (vorname) return { bezeichnung: 'Dein Nachname', ort: BENUTZER };
  if (nachname) return { bezeichnung: 'Dein Vorname', ort: BENUTZER };
  return { bezeichnung: 'Dein Vor- und Nachname', ort: BENUTZER };
}

/**
 * Fehlende Angaben über alle gezeigten Dokumente, je Angabe einmal (Reihenfolge des ersten Auftretens).
 * Unbekannte Platzhalter (nicht mehr im Katalog) stehen als Text in der Vorschau und werden ebenfalls gemeldet.
 */
export function fehlendeAngaben(
  dokumente: Dokumentangaben[],
  werte: Readonly<Partial<Record<string, string>>>,
  bearbeiter: Bearbeitername,
): FehlendeAngabe[] {
  const ergebnis = new Map<string, FehlendeAngabe>();
  const merken = (schluessel: string, angabe: Angabe, stelle: string) => {
    const vorhanden = ergebnis.get(schluessel);
    if (vorhanden) {
      if (!vorhanden.stellen.includes(stelle)) vorhanden.stellen.push(stelle);
    } else {
      ergebnis.set(schluessel, { schluessel, ...angabe, stellen: [stelle] });
    }
  };
  dokumente.forEach(({ stelle, dokument, unbekanntePlatzhalter = [] }) => {
    verwendetePlatzhalter(dokument).forEach((schluessel) => {
      if (NIE_FEHLEND.has(schluessel)) return;
      if (schluessel === 'VERTRAGSPARTNER') {
        fehlendeVertragspartnerAngaben(werte).forEach((angabe) => merken(angabe, ANGABEN[angabe], stelle));
        return;
      }
      if (schluessel === 'BEARBEITER') {
        const angabe = bearbeiterAngabe(bearbeiter);
        if (angabe) merken(schluessel, angabe, stelle);
        return;
      }
      if ((werte[schluessel] ?? '').trim() === '') {
        merken(schluessel, ANGABEN[schluessel] ?? { bezeichnung: schluessel, ort: PERSOENLICHES }, stelle);
      }
    });
    unbekanntePlatzhalter.forEach((schluessel) =>
      merken(
        `unbekannt:${schluessel}`,
        {
          bezeichnung: `Unbekannter Platzhalter {{${schluessel}}}`,
          ort: 'in der Vorlage unter Einstellungen › Vorlagen › Vertragsvorlagen prüfen',
        },
        stelle,
      ),
    );
  });
  return Array.from(ergebnis.values());
}

/** „IBAN – im Vertrag (beim Berater unter „Finanzen“ eintragen)“ */
export function fehlendeAngabeText(angabe: FehlendeAngabe): string {
  return `${angabe.bezeichnung} – ${angabe.stellen.join(' und ')} (${angabe.ort})`;
}

/**
 * RITA-1552: Bezeichnungen der Angaben, die beim Anlegen des Entwurfs leer waren (`advisor_values` mit leerem Wert),
 * in der gespeicherten Reihenfolge; die „abweichenden“ Angaben und das heutige Datum zählen nie (NIE_FEHLEND). Ohne
 * gemerkte Werte (null) gibt es keine Rückfrage.
 */
export function fehlendeAngabenAusGemerkten(advisorValues: Readonly<Record<string, string>> | null): string[] {
  if (!advisorValues) return [];
  return Object.entries(advisorValues)
    .filter(([schluessel, wert]) => !NIE_FEHLEND.has(schluessel) && (wert ?? '').trim() === '')
    .map(([schluessel]) => angabeBezeichnung(schluessel));
}
